import React from 'react';
import type {List} from 'immutable';
import {Link} from 'react-router';
import {Dropdown, IconButton, ExpandableDropdownItem, DropdownItem, Text} from '@albert-io/atomic';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {renderMoment} from 'lib/timeUtils';

import {callTargetedAction} from 'client/framework';
import questionTypeActions from 'components/QuestionTypes/questionType.actions';
import {isLegacyStore} from 'components/QuestionTypes/V2/useQuestionStores';
import type {GuessModelV1} from 'resources/augmented/Guess/GuessModel.v1';
import type {QuestionModelV3} from 'resources/augmented/Question/QuestionModel.v3';

import {makePreviousAttemptsQuery} from '../practiceView.utils';

interface BasePreviousAttemptsProps {
  onGuessClick: (guess: GuessModelV1) => void;
  // Props below are provided automatically and do not need to be passed in
  closeDropdown: () => void;
  guesses: List<GuessModelV1>;
  question: QuestionModelV3;
}

const PreviousAttempts = awaitMandarkQueries(
  (props) => {
    const {question} = props as {question: QuestionModelV3};
    return {
      queries: {
        guesses: makePreviousAttemptsQuery(question.getId())
      }
    };
  },
  class BasePreviousAttempts extends React.Component<BasePreviousAttemptsProps> {
    handleGuessClick(guess: GuessModelV1) {
      // Zustand-backed question types (item-bank, ordering) load the guess via
      // props (InputArea's effect) once onGuessClick sets the active guess.
      const store = this.props.question.getStore();
      if (isLegacyStore(store)) {
        callTargetedAction({
          name: questionTypeActions.LOAD_GUESS,
          targetStore: store.getName(),
          payload: guess
        });
      }
      this.props.onGuessClick(guess);
    }

    render() {
      const {guesses, closeDropdown} = this.props;
      return guesses.isEmpty() ? null : (
        <ExpandableDropdownItem label='Attempts' closeDropdown={closeDropdown}>
          {
            guesses.map((guess, i) => (
              <DropdownItem
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                onClick={() => this.handleGuessClick(guess)}
                icon={guess.isCorrect() ? 'check' : 'times'}
                iconColor={guess.isCorrect() ? 'positive' : 'negative'}
              >
                <Text bold size='s'>
                  {renderMoment(guess.getInsertedAt(), 'date')}
                </Text>
              </DropdownItem>
            )) as unknown as React.ReactNode
          }
        </ExpandableDropdownItem>
      );
    }
  }
);

/**
 * The closeDropdown prop is passed by the parent component automatically but the onClick needs to be passed down
 */

interface SaveToItemProps {
  closeDropdown?: () => void;
  onClick: () => void;
  disabled?: boolean;
}

const SaveToItem = ({closeDropdown, onClick, disabled}: SaveToItemProps) => (
  <DropdownItem closeDropdown={closeDropdown} onClick={onClick} disabled={disabled}>
    Save To...
  </DropdownItem>
);

/**
 * @param {Object} props
 * @param {() => void} props.closeDropdown Passed by parent Dropdown component
 * @param {string} props.editPath Path to the question in its edit mode. Authoring for authors, Dennis queue for supers.
 */

interface EditQuestionItemProps {
  closeDropdown?: () => void;
  editPath?: string | null;
}

const EditQuestionItem = ({closeDropdown, editPath}: EditQuestionItemProps) => {
  return (
    <DropdownItem
      preventDefaultClickHandler={false}
      as={Link}
      closeDropdown={closeDropdown}
      to={editPath}
    >
      Edit
    </DropdownItem>
  );
};

export default class HeadingActions extends React.Component<{children?: React.ReactNode}> {
  static PreviousAttempts = PreviousAttempts;

  static EditQuestionItem = EditQuestionItem;

  static SaveToItem = SaveToItem;

  render() {
    return (
      <Dropdown
        className='z-dropdown ml-x1'
        position='bottom-start'
        trigger={<IconButton icon='ellipsis-v' size='s' label='Question actions' />}
      >
        {}
        {React.Children.map(this.props.children, (child) =>
          React.cloneElement(child as React.ReactElement, this.props)
        )}
      </Dropdown>
    );
  }
}
